import { CommonModule } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { GenVideoPreviewData } from '../../../../../../../models/google-template/preview-interfaces/gen-video-preview-data.interface';
import { UrlDisplayHelper } from '../../../../../../../utils/url-display-helper';
import { VideoFormat, YoutubeFormatHelper } from '../../../../../../../utils/youtube-format-helper';
import { DesktopFrameComponent } from '../../../preview-common/desktop-frame/desktop-frame.component';
import { YoutubeNavbarComponent } from '../../../preview-common/youtube/youtube-navbar/youtube-navbar.component';

/**
 * Skippable in-stream ad on the desktop watch page. The creative runs in the
 * player with the advertiser card over its bottom-left corner, and the same
 * creative runs again as the companion banner at the top of the sidebar. The
 * 16:9 placement.
 */
@Component({
    selector: 'app-gen-youtube-watch-ad-desktop',
    imports: [CommonModule, DesktopFrameComponent, YoutubeNavbarComponent],
    templateUrl: './youtube-watch-ad-desktop.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './youtube-watch-ad-desktop.component.scss'
})
export class YoutubeWatchAdDesktopPreviewComponent {
  @Input() data!: GenVideoPreviewData;

  /** The creative format this placement previews, found by measured ratio. */
  readonly format: VideoFormat = '16:9';

  /** The sidebar's stubbed up-next list, which is four identical rows. */
  readonly upNextRows = [0, 1, 2, 3];

  /** Thumbnail of the 16:9 video, or '' when there isn't one yet. */
  get thumbnailUrl(): string {
    return YoutubeFormatHelper.thumbnailForFormat(this.videoIds, this.format);
  }

  /**
   * True while any link is still being measured, so the empty player says so
   * rather than asking for a video that is about to turn up.
   */
  get isResolving(): boolean {
    return YoutubeFormatHelper.isResolvingAny(this.videoIds);
  }

  /** The host, which is all either card shows of the destination. */
  get displayUrl(): string {
    return UrlDisplayHelper.hostOf(this.data.url);
  }

  /**
   * Both cards give the headline one line, so it takes the short headline and
   * only falls back to the long one when there isn't one.
   */
  get headline(): string {
    return this.data.headlines[0] || this.data.longHeadlines[0] || 'Your headline appears here';
  }

  private get videoIds(): string[] {
    return this.data.videos.map((video) => video.videoId);
  }
}
